import {
  AutocompleteMenuSelector,
  AutocompleteSelectorOptionCoinWithImage,
  FormGroup,
  SelectInputWithImage,
} from '@starsoft/common/components';
import {
  useNavigate,
  useSearchParams,
  useTranslation,
} from '@starsoft/common/hooks';
import { Coin, CoinType } from '@starsoft/common/models';
import { useFormContext } from 'react-hook-form';
import { DepositCoinSelectProps } from './props';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { useCurrentModal } from '../../../../../../../../hooks/modals/useCurrentModal';
import useDepositSearchParams from '../../../../../../../../hooks/deposit/useDepositSearchParams';
import { useDepositForm } from '../../../../../../../../hooks/deposit';
import AutocompleteSelectorOptionWithImageSkeleton from '../../../../../../../AutocompleteMenuSelector/CustomOptions/AutocompleteSelectorAccount/Skeleton';
import SelectInputWithImageSkeleton from '../../../../../../../SelectInput/SelectWithImage/SelectInputWithImage/Skeleton';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { CommonModalsKey, DepositMethodId } from '@starsoft/common/enums';

/**
 * DepositCoinSelect Component
 * This component is responsible for rendering a coin selection input within a form.
 * It uses various hooks to manage form state, navigation, and search parameters.
 *
 * @param {DepositCoinSelectProps} props - The properties for the component.
 * @param {boolean} props.hideInput - Flag indicating whether to hide the input field.
 *
 * @returns {JSX.Element | null} The rendered component displaying the coin selection input or null if hideInput is true.
 *
 * @example
 * // Usage example:
 * <DepositCoinSelect hideInput={false} />
 */
export default function DepositCoinSelect({
  hideInput,
}: DepositCoinSelectProps) {
  const { t } = useTranslation('deposit');
  const { watch, setValue } = useFormContext<CreateDepositRequestDto>();
  const search = useSearchParams();
  const modal = useCurrentModal(CommonModalsKey);
  const navigate: NavigateFunction = useNavigate();
  const {
    coinId: searchCoinId,
    depositMethodId,
    coinType,
  } = useDepositSearchParams();
  const { isFormLoading, coinOptions, currentChain } = useDepositForm(
    watch,
    setValue,
  );
  const coinId = watch('coinId') ?? searchCoinId;

  /**
   * Handles the selection of a coin.
   * Updates the search parameters and form values based on the selected coin.
   *
   * @param {Coin} coin - The selected coin object.
   *
   * @example
   * handleSelectCoin({ id: '1', name: 'Bitcoin', code: 'BTC' });
   */
  function handleSelectCoin(coin: Coin): void {
    search.set('coin-id', `${coin?.id}`);
    setValue('coinId', coin?.id?.toString(), {
      shouldDirty: false,
    });
    setValue('coin', coin, {
      shouldDirty: false,
    });
    search.delete('method-id');
    navigate({
      search: search.toString(),
      hash: modal,
    });
  }

  if (hideInput) {
    return null;
  }

  return (
    <FormGroup
      label={t('crypto_form_coin_label')}
      loading={{ label: true, secondary: false }}
      isLoading={isFormLoading}
    >
      <AutocompleteMenuSelector
        title=""
        options={coinOptions || []}
        value={coinId}
        filterBy={['symbol', 'name']}
        setValue={handleSelectCoin}
        getOptionLabel={option => option.code}
        getOptionValue={option => option.id?.toString()}
        loading={isFormLoading || !coinId}
        customComponents={{
          customOption: ({ handleSelect, option, selected }) => (
            <AutocompleteSelectorOptionCoinWithImage
              handleSelect={handleSelect}
              option={option}
              selected={selected}
              isContainerColor
            />
          ),
          customInput: ({ handleOpen, open, option }) => (
            <SelectInputWithImage
              handleOpen={handleOpen}
              open={open}
              pairImage={
                (depositMethodId == DepositMethodId.Crypto?.toString() ||
                  coinType == CoinType.Crypto) &&
                currentChain
                  ? `${SettingsService.env.S3_BUCKET_BASE_URL}/chains/rounded/${currentChain?.image}`
                  : undefined
              }
              cardBg
              image={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${option?.image}`}
              label={`${option?.name} <strong> ${option?.code}</strong>`}
            />
          ),
          customSkeletonOption: () => (
            <AutocompleteSelectorOptionWithImageSkeleton />
          ),
          customSkeletonInput: () => <SelectInputWithImageSkeleton cardBg />,
        }}
        isContainerColor
      />
    </FormGroup>
  );
}
